import {
  type GetDataRoomNodesQueryResult,
  useGetDataRoomNodes,
} from "@vega-api/generated/coinvest-deal-data-room-nodes/coinvest-deal-data-room-nodes";
import {
  type GetDealGeneralPartnerViewV3QueryResult,
  useGetDealGeneralPartnerViewV3,
} from "@vega-api/generated/deal/deal";
import type { DataRoomDealOpportunity } from "@vega-api/generated/types/dataRoomDealOpportunity";

import type { ParsedDataRoom } from "../components/DataRoomPermissions/types";
import { parseApiResponse } from "../components/DataRoomPermissions/utils/treeUtils";

function selectDataRoomIdFromDeal(
  data: GetDealGeneralPartnerViewV3QueryResult,
): string | undefined {
  return data.data?.deal.vegaDataRooms?.[0]?.id;
}

function selectDataRoomNodesView(apiResponse: GetDataRoomNodesQueryResult) {
  const response = apiResponse.data;

  return {
    dataRoomName: response.dataRoom.name,
    dealOpportunities: [...response.dealOpportunities].sort((a, b) =>
      a.limitedPartner.name.localeCompare(b.limitedPartner.name),
    ),
    parsedData: parseApiResponse(response),
  };
}

const EMPTY_PARSED_DATA_ROOM: ParsedDataRoom = {
  nodeMap: {},
  access: {},
  downloadable: {},
};

type UseDataRoomDataReturn = {
  dataRoomId: string | undefined;
  dataRoomName: string;
  dealOpportunities: DataRoomDealOpportunity[];
  parsedData: ParsedDataRoom;
  isLoading: boolean;
  isError: boolean;
};

export function useDataRoomData(dealId: string): UseDataRoomDataReturn {
  const { data: dataRoomId, isLoading: isDealLoading } =
    useGetDealGeneralPartnerViewV3(dealId, {
      query: {
        enabled: !!dealId,
        select: selectDataRoomIdFromDeal,
      },
    });

  const {
    data: selected,
    isLoading: isNodesLoading,
    isError,
  } = useGetDataRoomNodes(dealId, dataRoomId ?? "", {
    query: {
      enabled: !!dataRoomId,
      select: selectDataRoomNodesView,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  return {
    dataRoomId,
    dataRoomName: selected?.dataRoomName ?? "",
    dealOpportunities: selected?.dealOpportunities ?? [],
    parsedData: selected?.parsedData ?? EMPTY_PARSED_DATA_ROOM,
    isLoading: isDealLoading || isNodesLoading,
    isError,
  };
}
